/**
 * Lógica pura de clasificación de aptitud preanalítica.
 * Sin dependencias React — facilita testing con fast-check (PBT).
 *
 * Requirements: M4.4.2, M4.15.3
 */

import type { ClasificacionAptitud } from "@/hooks/use-muestras";

// =============================================================================
// FUNCIONES PURAS
// =============================================================================

/**
 * Retorna el color CSS asociado a cada clasificación de aptitud.
 * Usado para colorear los badges en el panel de validación.
 */
export function getColorClasificacion(clasificacion: ClasificacionAptitud): string {
  const colores: Record<ClasificacionAptitud, string> = {
    APTO: "text-green-600",
    APTO_CON_OBSERVACION: "text-yellow-600",
    NO_APTO: "text-red-600",
    PENDIENTE_POR_TIEMPO: "text-gray-500",
  };
  return colores[clasificacion] ?? "text-gray-500";
}

/**
 * Retorna el variant de Badge para cada clasificación.
 */
export function getBadgeVariantClasificacion(
  clasificacion: ClasificacionAptitud
): "default" | "secondary" | "destructive" | "outline" {
  const variantes: Record<
    ClasificacionAptitud,
    "default" | "secondary" | "destructive" | "outline"
  > = {
    APTO: "default",
    APTO_CON_OBSERVACION: "outline",
    NO_APTO: "destructive",
    PENDIENTE_POR_TIEMPO: "secondary",
  };
  return variantes[clasificacion] ?? "secondary";
}

/**
 * Retorna el ícono (nombre de lucide-react) para cada clasificación.
 */
export function getIconClasificacion(
  clasificacion: ClasificacionAptitud
): "CheckCircle" | "AlertCircle" | "XCircle" | "Clock" {
  const iconos: Record<ClasificacionAptitud, "CheckCircle" | "AlertCircle" | "XCircle" | "Clock"> =
    {
      APTO: "CheckCircle",
      APTO_CON_OBSERVACION: "AlertCircle",
      NO_APTO: "XCircle",
      PENDIENTE_POR_TIEMPO: "Clock",
    };
  return iconos[clasificacion] ?? "AlertCircle";
}

/**
 * Verifica si un examen es apto para toma (puede proceder con la extracción).
 * APTO y APTO_CON_OBSERVACION permiten continuar.
 * NO_APTO requiere override manual.
 * PENDIENTE_POR_TIEMPO requiere esperar.
 */
export function esAptoParaToma(clasificacion: ClasificacionAptitud): boolean {
  return clasificacion === "APTO" || clasificacion === "APTO_CON_OBSERVACION";
}

/**
 * Verifica si un examen requiere atención especial (observación u override).
 */
export function requiereAtencion(clasificacion: ClasificacionAptitud): boolean {
  return clasificacion === "APTO_CON_OBSERVACION" || clasificacion === "NO_APTO";
}

/**
 * Retorna el texto descriptivo para cada clasificación.
 */
export function getTextoClasificacion(clasificacion: ClasificacionAptitud): string {
  const textos: Record<ClasificacionAptitud, string> = {
    APTO: "Apto",
    APTO_CON_OBSERVACION: "Apto con observación",
    NO_APTO: "No apto",
    PENDIENTE_POR_TIEMPO: "Pendiente por tiempo",
  };
  return textos[clasificacion] ?? clasificacion;
}
